// ContactEditMobileActivitySheet — the entire mobile Activity bottom-sheet
// extracted verbatim from ContactEditModal.tsx's modalContent JSX
// (the `{isMobile && showMobileActivity && (...)}` block). Header-with-back +
// the mobile timeline IIFE + the mobile composer.
//
// The mobile composer's inline async send (which contained the 3rd raw `fetch`)
// is delegated to `onSend` — the orchestrator's NEW sendContactEmailMobile()
// handler, co-located beside sendContactEmail — so this component has ZERO
// `fetch`. The note branch still uses handleAddNote + setComposerMode as before.
//
// The mobile timeline IIFE defines its OWN local types (MobileTimelineItem etc.)
// and is moved wholesale so its closures stay intact. Markup/handlers/inline
// styles are byte-identical to the inline original.
import React from 'react';
import { stripHtmlToText } from '../utils';
import { isAutomatedSender } from '../lib/contactTimeline';
import { type EmailAttachment } from '../lib/emailAttachments';
import type { Activity } from '../lib/types';

type GmailAttachment = { filename: string; mimeType: string; size?: number; attachmentId: string };

interface GmailMessageLike {
  id: string;
  from: string;
  date: string;
  body: string;
  subject: string;
  threadId: string;
  attachments?: GmailAttachment[];
}

export interface ContactEditMobileActivitySheetProps {
  mobileKeyboardHeight: number;
  setShowMobileActivity: (b: boolean) => void;
  name: string;
  firstName: string;
  lastName: string;
  // `type` widened to string so the verbatim-lifted timeline can compare against
  // 'email-sent'/'email-received' (legacy activity-type strings) exactly as the
  // original inline IIFE did — Activity[] is assignable here.
  activities: Array<Omit<Activity, 'type'> & { type?: string }>;
  gmailMessages: GmailMessageLike[];
  profile: { id?: string; email?: string } | null | undefined;
  gmailAccountEmails: string[];
  composerMode: 'reply' | 'compose' | 'note' | null;
  setComposerMode: (m: 'reply' | 'compose' | 'note' | null) => void;
  composerCanReply: boolean;
  composerLastSubject: string;
  connectedMailboxes: string[];
  noteType: 'note' | 'call' | 'email' | 'meet';
  setNoteType: (t: 'note' | 'call' | 'email' | 'meet') => void;
  composeSubject: string;
  setComposeSubject: (s: string) => void;
  composeFrom: string;
  setComposeFrom: (s: string) => void;
  newNote: string;
  setNewNote: (s: string) => void;
  composerFocused: boolean;
  setComposerFocused: (b: boolean) => void;
  emailAttachments: EmailAttachment[];
  setEmailAttachments: React.Dispatch<React.SetStateAction<EmailAttachment[]>>;
  mobileEmailFileInputRef: React.RefObject<HTMLInputElement | null>;
  handleEmailFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removeEmailAttachment: (id: string) => void;
  handleAddNote: () => void;
  onSend: () => void | Promise<void>;
}

export function ContactEditMobileActivitySheet({
  mobileKeyboardHeight, setShowMobileActivity, name, firstName, lastName,
  activities, gmailMessages, profile, gmailAccountEmails,
  composerMode, setComposerMode, composerCanReply, composerLastSubject, connectedMailboxes,
  noteType, setNoteType, composeSubject, setComposeSubject, composeFrom, setComposeFrom,
  newNote, setNewNote, composerFocused, setComposerFocused, emailAttachments, setEmailAttachments,
  mobileEmailFileInputRef, handleEmailFileSelect, removeEmailAttachment, handleAddNote, onSend,
}: ContactEditMobileActivitySheetProps) {
  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0,
        bottom: mobileKeyboardHeight,
        background: 'var(--color-surface)',
        zIndex: 2147483646, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
      }}
      onClick={() => setShowMobileActivity(false)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'flex', flexDirection: 'column',
          background: 'var(--color-surface)', borderRadius: 0,
          height: '100%', overflow: 'hidden',
        }}
      >
        {/* Header with back */}
        <div style={{
          flexShrink: 0, padding: '0 16px', borderBottom: '1px solid #DADCE0',
          display: 'flex', alignItems: 'center', gap: 4,
          height: 44, marginTop: 'max(env(safe-area-inset-top, 0px), 6px)',
          boxSizing: 'border-box',
        }}>
          <button
            type="button"
            onClick={() => setShowMobileActivity(false)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, borderRadius: 8, border: 'none',
              background: 'none', cursor: 'pointer', color: 'var(--color-brand)',
              marginLeft: -8, flexShrink: 0,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
          <span
            onClick={() => setShowMobileActivity(false)}
            style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-brand)', cursor: 'pointer' }}
          >Back</span>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text)' }}>
            {name || `${firstName} ${lastName}`.trim() || 'Activity'}
          </span>
        </div>

        {/* Chat-style activity timeline */}
        <div className="xv-scroll" style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '12px 16px', minHeight: 0 }}>
          {(() => {
            type MobileTimelineItem = {
              id: string;
              date: string;
              kind: 'email-sent' | 'email-received' | 'activity';
              body: string;
              subject?: string;
              threadId?: string;
              attachments?: GmailAttachment[];
              gmailMessageId?: string;
            };
            type MobileThreadGroup = { threadId: string; subject: string; date: string; messages: MobileTimelineItem[] };
            type MobileEntry = { entryType: 'thread'; data: MobileThreadGroup; date: string } | { entryType: 'activity'; data: MobileTimelineItem; date: string };

            const mActivityItems: MobileTimelineItem[] = [];
            for (const a of activities) {
              const isEmailType = a.type === 'email' || a.type === 'email-sent' || a.type === 'email-received';
              if (isEmailType && gmailMessages.length > 0) continue;
              const mIsFromContact = /^Email from /i.test(a.note) || (a.authorId && a.authorId !== profile?.id);
              mActivityItems.push({
                id: `a-${a.id}`, date: a.date,
                kind: isEmailType ? (mIsFromContact ? 'email-received' : 'email-sent') : 'activity',
                body: isEmailType ? a.note.replace(/^Email (?:to|from) [^:]+:\s*/i, '').trim() : a.note,
              });
            }

            const mUserEmailList = [...new Set([profile?.email, ...gmailAccountEmails].filter(Boolean))].map(e => e!.toLowerCase());
            const mThreadMap = new Map<string, MobileThreadGroup>();
            for (const gm of gmailMessages) {
              if (isAutomatedSender(gm.from)) continue;
              const strippedBody = stripHtmlToText(gm.body);
              const fromLower = gm.from.toLowerCase();
              const isFromMe = mUserEmailList.some((ue) => fromLower.includes(ue));
              const isSent = isFromMe;
              const tid = gm.threadId || gm.id;
              const msg: MobileTimelineItem = {
                id: `gm-${gm.id}`, date: gm.date,
                kind: isSent ? 'email-sent' : 'email-received',
                body: strippedBody.length > 300 ? strippedBody.substring(0, 300) + '...' : strippedBody,
                subject: gm.subject, threadId: tid,
                attachments: gm.attachments, gmailMessageId: gm.id,
              };
              if (!mThreadMap.has(tid)) {
                mThreadMap.set(tid, { threadId: tid, subject: (gm.subject || '').replace(/^(Re|Fwd):\s*/i, ''), date: gm.date, messages: [] });
              }
              mThreadMap.get(tid)!.messages.push(msg);
            }
            for (const tg of mThreadMap.values()) {
              tg.messages.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
              tg.date = tg.messages[0].date;
            }

            const mCombined: MobileEntry[] = [];
            for (const tg of mThreadMap.values()) mCombined.push({ entryType: 'thread', data: tg, date: tg.date });
            for (const ai of mActivityItems) mCombined.push({ entryType: 'activity', data: ai, date: ai.date });
            mCombined.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

            if (mCombined.length === 0) {
              return (
                <div style={{ textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 14, padding: '40px 20px' }}>
                  No activity yet
                </div>
              );
            }

            return mCombined.map(entry => {
              if (entry.entryType === 'thread') {
                const thread = entry.data;
                return (
                  <div key={thread.threadId} style={{ marginBottom: 12 }}>
                    {thread.subject && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 6px' }}>
                        <div style={{ flex: 1, height: 1, background: '#DADCE0' }} />
                        <span style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 500, whiteSpace: 'nowrap', maxWidth: '70%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {thread.subject}
                        </span>
                        <div style={{ flex: 1, height: 1, background: '#DADCE0' }} />
                      </div>
                    )}
                    {thread.messages.map(msg => {
                      const msgDate = new Date(msg.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + new Date(msg.date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                      return (
                        <div key={msg.id} style={{ marginBottom: 6, display: 'flex', flexDirection: 'column', alignItems: msg.kind === 'email-sent' ? 'flex-end' : 'flex-start' }}>
                          <div style={{
                            maxWidth: '85%', padding: '10px 14px', borderRadius: 12,
                            background: msg.kind === 'email-sent' ? '#D6E4F7' : '#D3D6DC',
                            fontSize: 13, lineHeight: 1.5, color: 'var(--color-text)',
                          }}>
                            {msg.body}
                            {msg.attachments && msg.attachments.length > 0 && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: 12, color: 'var(--color-text-muted)' }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                                </svg>
                                {msg.attachments.length === 1 ? msg.attachments[0].filename : `${msg.attachments.length} attachments`}
                              </div>
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 3, padding: '0 4px' }}>{msgDate}</div>
                        </div>
                      );
                    })}
                  </div>
                );
              }
              // Activity
              const item = entry.data;
              // Email-type activity — blue bubble
              if (item.kind === 'email-sent' || item.kind === 'email-received') {
                const isSent = item.kind === 'email-sent';
                return (
                  <div key={item.id} style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', alignItems: isSent ? 'flex-end' : 'flex-start' }}>
                    <div style={{ maxWidth: '85%', padding: '10px 14px', borderRadius: 12, background: isSent ? '#D6E4F7' : '#D3D6DC', fontSize: 13, lineHeight: 1.5, color: 'var(--color-text)' }}>
                      {item.body}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 3, padding: '0 4px' }}>
                      {new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      {' '}
                      {new Date(item.date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </div>
                  </div>
                );
              }
              return (
                <div key={item.id} style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <div style={{ maxWidth: '85%', padding: '10px 14px', borderRadius: 12, background: '#FEF3C7', fontSize: 13, lineHeight: 1.5, color: 'var(--color-text)' }}>
                    {item.body}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 3, padding: '0 4px' }}>
                    {new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    {' '}
                    {new Date(item.date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </div>
                </div>
              );
            });
          })()}
        </div>

        {/* Composer — Send Email / Internal Note tabs */}
        <div style={{ borderTop: '1px solid #DADCE0', flexShrink: 0, background: 'var(--color-surface)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          {composerMode === null ? (
            <div style={{ display: 'flex', gap: 8, padding: '10px 12px' }}>
              {composerCanReply && (
                <button type="button" onClick={() => { setNoteType('email'); setComposerMode('reply'); }} style={{ flex: 1, padding: '10px 0', fontSize: 14, fontWeight: 600, color: '#3C4043', background: 'var(--color-surface)', border: '1px solid #DADCE0', borderRadius: 8, cursor: 'pointer' }}>Reply</button>
              )}
              <button type="button" onClick={() => { setNoteType('email'); setComposeSubject(''); setComposeFrom(connectedMailboxes.length >= 2 ? (connectedMailboxes[0] || '') : ''); setComposerMode('compose'); }} style={{ flex: 1, padding: '10px 0', fontSize: 14, fontWeight: 600, color: '#fff', background: 'var(--color-brand)', border: '1px solid var(--color-brand)', borderRadius: 8, cursor: 'pointer' }}>Compose</button>
              <button type="button" onClick={() => { setNoteType('note'); setComposerMode('note'); }} style={{ flex: 1, padding: '10px 0', fontSize: 14, fontWeight: 600, color: '#3C4043', background: 'var(--color-surface)', border: '1px solid #DADCE0', borderRadius: 8, cursor: 'pointer' }}>Note</button>
            </div>
          ) : (
          <>
          {/* Active composer: header + cancel */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px 0' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{composerMode === 'reply' ? 'Reply' : composerMode === 'compose' ? 'New email' : 'Note'}</span>
            <button type="button" onClick={() => { setComposerMode(null); setNewNote(''); setComposeSubject(''); setEmailAttachments([]); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: 13 }}>Cancel</button>
          </div>
          {composerMode === 'compose' && (
            <div style={{ padding: '8px 14px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <input value={composeSubject} onChange={(e) => setComposeSubject(e.target.value)} placeholder="Subject" style={{ width: '100%', padding: '10px 12px', fontSize: 16, border: '1px solid #DADCE0', borderRadius: 8, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', background: 'var(--color-surface)', color: 'var(--color-text)' }} />
              {connectedMailboxes.length >= 2 && (
                <select value={composeFrom || connectedMailboxes[0]} onChange={(e) => setComposeFrom(e.target.value)} style={{ width: '100%', padding: '9px 12px', fontSize: 16, border: '1px solid #DADCE0', borderRadius: 8, boxSizing: 'border-box', background: 'var(--color-surface)', color: 'var(--color-text)' }}>
                  {connectedMailboxes.map(m => <option key={m} value={m}>From: {m}</option>)}
                </select>
              )}
            </div>
          )}
          {composerMode === 'reply' && composerLastSubject && (
            <div style={{ padding: '6px 14px 0', fontSize: 12, color: 'var(--color-text-muted)' }}>{composerLastSubject}</div>
          )}
          {/* Input + send — ChatBox-style bordered container */}
          <input ref={mobileEmailFileInputRef} type="file" multiple onChange={handleEmailFileSelect} style={{ display: 'none' }} />
          <div style={{ padding: '8px 14px' }}>
            <div style={{
              border: composerFocused ? '1.5px solid var(--color-brand)' : '1.5px solid #DADCE0',
              borderRadius: 12, overflow: 'hidden', background: 'var(--color-surface)',
              transition: 'border-color 0.15s',
            }}>
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder={noteType === 'email' ? 'Compose an email...' : 'Write a note...'}
                rows={composerMode === 'note' ? 2 : 4}
                style={{
                  width: '100%', padding: '12px 14px', fontSize: 16,
                  border: 'none', resize: 'none',
                  outline: 'none', fontFamily: 'inherit', background: 'var(--color-surface)', color: 'var(--color-text)',
                  lineHeight: 1.4, display: 'block',
                }}
                onFocus={() => { setComposerFocused(true); setTimeout(() => document.activeElement?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300); }}
                onBlur={() => setComposerFocused(false)}
              />
              {/* Email attachments preview */}
              {noteType === 'email' && emailAttachments.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '0 14px 6px' }}>
                  {emailAttachments.map(att => (
                    <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, border: '1px solid #DADCE0', background: '#F8F9FA', fontSize: 12 }}>
                      {att.previewUrl ? <img src={att.previewUrl} alt="" style={{ width: 16, height: 16, objectFit: 'cover', borderRadius: 2 }} /> : (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#7a7f85" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      )}
                      <span style={{ maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#3C4043' }}>{att.filename}</span>
                      <button onClick={() => removeEmailAttachment(att.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--color-text-muted)', fontSize: 13, lineHeight: 1 }}>×</button>
                    </div>
                  ))}
                </div>
              )}
              {/* Toolbar row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', borderTop: '1px solid #F1F3F4' }}>
                {/* Left: attach button (email only) */}
                <div>
                  {noteType === 'email' && (
                    <button
                      type="button"
                      onClick={() => mobileEmailFileInputRef.current?.click()}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        padding: 4, color: 'var(--color-text-muted)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                      </svg>
                    </button>
                  )}
                </div>
                {/* Right: send button */}
                <button
                  type="button"
                  disabled={!newNote.trim()}
                  onClick={async () => {
                    if (noteType === 'email') {
                      await onSend();
                    } else {
                      handleAddNote();
                      setComposerMode(null);
                    }
                  }}
                  style={{
                    width: 44, height: 44, borderRadius: 10, border: 'none',
                    background: newNote.trim() ? 'var(--color-brand)' : '#DADCE0',
                    color: newNote.trim() ? '#fff' : '#7a7f85',
                    cursor: newNote.trim() ? 'pointer' : 'not-allowed',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, padding: 0,
                  }}
                >
                  {noteType === 'email' ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
